// SPDX-FileCopyrightText: Copyright 2026 OP Pohjola (https://op.fi). All Rights Reserved.
// SPDX-License-Identifier: LicenseRef-OpPohjolaAllRightsReserved

import React, { useRef, useContext, useEffect, useCallback } from 'react';
import _ from 'lodash';
import messagesFi from '../messages/messages_fi.properties';
import messagesSv from '../messages/messages_sv.properties';
import messagesEn from '../messages/messages_en.properties';
import { AuthMessageProvider, Login as AuthComponentsLogin, PasswordChangeRequiredError } from '@op/auth-components/login';
import TrustNetworkLogin from './TrustNetworkLogin';
import VerificationCancelButton from './VerificationCancelButton';
import api from '../api';
import { StateContext, ChangeStateContext, initialState } from '../State';
import { Verify } from '@op/auth-components/verify';
import AutomaticLogoutModal from './AutomaticLogoutModal';
import loadingIndicator from '../loadingIndicator';
import { Mode, RepMode, ErrorCode, LoginUiPhase, loginContainerId, fullpageloginRootId } from '../constants';
import { ModelContext } from '../ModelContext';

const Login = (props) => {
    const loginRef = useRef(null);
    const state = useContext(StateContext);
    const changeState = useContext(ChangeStateContext);
    const locale = state.lang;
    const messages = state.messages;
    const { model } = useContext(ModelContext);

    const setError = useCallback(
        (errorCode) => {
            changeState({ errorCode: errorCode });
        },
        [changeState]
    );

    const resetState = useCallback(() => {
        /* Language may have changed after initialState was created, so we make sure current language and messages are not lost */
        changeState(_.merge({}, initialState(model), { lang: state.lang, messages: state.messages }));
    }, [changeState, model, state.lang, state.messages]);

    const isAuthMethodSupported = useCallback(
        (authMethod) => {
            /* Empty list means we support them all */
            if (!model.supportedAuthMethods?.length) {
                return true;
            }
            for (const supportedAuthMethod of model.supportedAuthMethods) {
                if (
                    authMethod.toLowerCase() === supportedAuthMethod.toLowerCase() ||
                    authMethod.toLowerCase() + '_otp' === supportedAuthMethod.toLowerCase()
                ) {
                    return true;
                }
            }
            return false;
        },
        [model.supportedAuthMethods]
    );

    const startRepresentationSelectionFlow = useCallback(() => {
        changeState({ mode: Mode.REP, repMode: RepMode.AUTO_SELECT });
    }, [changeState]);

    const onError = useCallback(
        async (err) => {
            console.debug('onError called:', err);
            if (err instanceof PasswordChangeRequiredError) {
                setError(ErrorCode.PASSWORD_CHANGE_REQUIRED);
                return null;
            }
            resetState();
            changeState({ errorDisplayed: true });
            return 'error';
        },
        [resetState, changeState, setError]
    );

    const onCompleted = useCallback(
        async (status, verify) => {
            console.debug('onCompleted called:', status, verify);
            loadingIndicator.set(true, false);

            if (!isAuthMethodSupported(status.authMethod)) {
                setError(ErrorCode.AUTH_METHOD_FORBIDDEN);
                return;
            }
            if ('authLevel' in status) {
                changeState({ currentAuthLevel: status.authLevel });
            } else {
                changeState({ currentAuthLevel: 2 });
            }
            setTimeout(startRepresentationSelectionFlow, 0);
        },
        [isAuthMethodSupported, setError, startRepresentationSelectionFlow, changeState]
    );

    const onVerificationData = useCallback(
        async (data, type) => {
            console.debug('onVerificationData', data, type);
            changeState({ authType: type === 'mobilekey' ? 'mobilekey' : 'keycode', errorDisplayed: false });
        },
        [changeState]
    );

    const onBeforeVerification = useCallback(
        async (data) => {
            console.debug('onBeforeVerification called', data);
            loadingIndicator.set(false);
            changeState({ loginUiPhase: LoginUiPhase.VERIFY });

            if (state.authType === 'keycode') {
                /* This gives focus to keycode input field. */
                setTimeout(() => {
                    loginRef.current?.focus();
                }, 0);
            }
        },
        [changeState, state.authType]
    );

    const isStrongAuthRequired = () => {
        return model.authLevel >= 3;
    };

    useEffect(() => {
        if (state.errorCode) {
            let errorMessage;
            switch (state.errorCode) {
                case ErrorCode.AUTH_METHOD_FORBIDDEN:
                case ErrorCode.REPRESENTATIONS_NOT_FOUND:
                case ErrorCode.ISVA_FORBIDDEN:
                    errorMessage = 'forbiddenError';
                    break;
                case ErrorCode.PASSWORD_CHANGE_REQUIRED:
                    errorMessage = 'passwordChangeRequired';
                    break;
                case ErrorCode.VERIFICATION_CANCEL:
                case ErrorCode.TRUSTNETWORK_CANCEL:
                    errorMessage = 'authenticationCancelError';
                    break;
                case ErrorCode.SELECT_REPRESENTATION_FAILED:
                    errorMessage = 'selectRepresentationError';
                    break;
                default:
                    errorMessage = 'applicationError';
                    break;
            }
            /* Provide all languages to Login component because it has internal logic to handle language changing */
            loginRef.current?.showAlert(
                {
                    fi: { title: messagesFi[errorMessage].title(), body: messagesFi[errorMessage].body() },
                    sv: { title: messagesSv[errorMessage].title(), body: messagesSv[errorMessage].body() },
                    en: { title: messagesEn[errorMessage].title(), body: messagesEn[errorMessage].body() },
                },
                'error'
            );
            /* Reset state after handling so next renders do not set alert over and over again. */
            /* Login component's internal logic removes the alert when user starts typing credentials. */
            resetState();
            changeState({ errorDisplayed: true });
            /* In case of error do a silent async logout */
            api.logout().catch((e) => {
                console.debug('error handler logout failed:', e);
            });
        }
        if (model.tamOp === 'error') {
            /* Convert ISVA TAM_OP error into fullpagelogin error */
            const url = new URL(window.location.href);
            url.searchParams.set('TAM_OP', 'login');
            url.searchParams.set('ERROR_CODE', '0x00000000');
            url.hash = `error/${encodeURIComponent(model.errorCode)}`;
            window.location.href = url.toString();
        } else if (window?.location.hash.startsWith('#error')) {
            /* Handle errors coming from ISVA, trustnetwork flow and possibly other external sources */
            const errorCode = window.location.hash.includes('/') ? window.location.hash.split('/')[1] : null;
            window.location.hash = '';
            if (errorCode) {
                setError(errorCode);
            }
        }
    });

    const ariaLive = state.errorDisplayed ? 'polite' : 'off';

    return (
        <>
            <div id={loginContainerId} aria-live={ariaLive}>
                <AuthMessageProvider locale={locale} messages={messages}>
                    <AuthComponentsLogin
                        appElement={document.getElementById(fullpageloginRootId)}
                        onError={onError}
                        weakLogin={!isStrongAuthRequired()}
                        onVerificationData={onVerificationData}
                        onCompleted={onCompleted}
                        onBeforeVerification={onBeforeVerification}
                        apiRootUrl=''
                        Verify={Verify}
                        ref={loginRef}
                        size={'hero'}
                        headingLevel={2}
                        {...props}
                    />
                </AuthMessageProvider>
                <VerificationCancelButton />
                <AutomaticLogoutModal />
            </div>
            {!state.authType && <TrustNetworkLogin />}
        </>
    );
};

export default Login;
